import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { useHardwareSnapshot } from "../../lib/hardwareStore";
import { Modal } from "../ui/Modal";

interface Offer { available: boolean; version?: string; notes?: string; notesAr?: string; size?: number; sha256?: string; url?: string }

/**
 * Update the Windows POS from inside the app. The server hands this terminal a signed link, the exact size and the SHA-256 of the newest
 * installer; the app downloads it, checks the fingerprint and only then runs it silently. The till restarts by itself in about a minute.
 */
export function UpdateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const hw = useHardwareSnapshot();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const bridge = window.hardware?.settings;

  useEffect(() => {
    let live = true;
    api.post<Offer>("/terminals/update-link", { appVersion: hw.version ?? "0.0.0" }).then((o) => live && setOffer(o)).catch(() => live && setError("OFFER"));
    return () => { live = false; };
  }, [hw.version]);
  useEffect(() => bridge?.onUpdateProgress?.((p) => setProgress(p)), [bridge]);

  async function install() {
    if (!bridge?.installUpdate || !offer?.url || !offer.sha256 || !offer.size || !offer.version) return;
    setBusy(true);
    setError(null);
    try {
      await bridge.installUpdate({ version: offer.version, url: offer.url, sha256: offer.sha256, size: offer.size });
      // the installer takes over from here: this window is closed by the update
    } catch (e) {
      const code = (e as { code?: string }).code ?? "DOWNLOAD_FAILED";
      setError(code);
      setBusy(false);
    }
  }
  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  const notes = offer ? (locale === "ar" && offer.notesAr ? offer.notesAr : offer.notes) : "";
  const mb = (n?: number) => (n ? `${(n / 1_048_576).toFixed(1)} MB` : "");

  return (
    <Modal open size="sm" onClose={busy ? () => undefined : onClose} title={t("update.title")}
      footer={<>
        <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}>{t("update.later")}</button>
        <button type="button" className="btn-primary" disabled={busy || !offer?.available || !bridge?.installUpdate} onClick={() => void install()}>{busy ? t("update.installing") : t("update.now")}</button>
      </>}>
      {!offer && !error ? <p className="text-xs font-bold text-gray-500">{t("common.loading")}</p> : !offer?.available ? (
        <p className="text-xs font-bold text-emerald-700" data-testid="update-none">✅ {error === "OFFER" ? t("update.checkFailed") : t("update.upToDate", { version: hw.version ?? "" })}</p>
      ) : (
        <>
          <div className="rounded-xl bg-purple-50 p-3 text-xs font-bold" data-testid="update-offer">
            <div className="flex justify-between"><span>{t("update.current")}</span><span className="num font-mono">{hw.version}</span></div>
            <div className="flex justify-between"><span>{t("update.new")}</span><span className="num font-mono text-cosmic-purple">{offer.version}</span></div>
            <div className="flex justify-between"><span>{t("update.size")}</span><span className="num font-mono">{mb(offer.size)}</span></div>
          </div>
          {notes && <p className="whitespace-pre-line text-xs font-semibold text-gray-600">{notes}</p>}
          <p className="rounded-xl bg-amber-50 p-2.5 text-[11px] font-bold text-amber-900">⚠️ {t("update.warning")}</p>
          {busy && (
            <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200"><div className="h-full rounded-full bg-cosmic-purple transition-all" style={{ width: `${pct}%` }} /></div>
              <p className="mt-1 text-[11px] font-bold text-gray-500">{pct < 100 ? t("update.downloading", { pct }) : t("update.verifying")}</p>
            </div>
          )}
          {!bridge?.installUpdate && <p className="text-[11px] font-bold text-red-600">{t("update.needsNewApp")}</p>}
        </>
      )}
      {error && error !== "OFFER" && <p className="text-[11px] font-bold text-red-600" role="alert">{t(`update.errors.${error}`, { defaultValue: t("update.errors.DOWNLOAD_FAILED") })}</p>}
    </Modal>
  );
}
